import { useState } from "react";
import { Phone, Menu, X, Clock3, Languages, MessageCircle } from "lucide-react";
import { useLang } from "@/context";
import { CONTACT, waLink } from "@/i18n";
import { scrollToId } from "@/lib/scroll";

const NAV = [
    { id: "home", key: "home" },
    { id: "services", key: "services" },
    { id: "how-it-works", key: "howItWorks" },
    { id: "areas", key: "areas" },
    { id: "faqs", key: "faqs" },
    { id: "contact", key: "contact" },
];

const LANGS = [
    { code: "en", label: "EN" },
    { code: "hi", label: "HI" },
    { code: "bn", label: "BN" },
];

export const Header = () => {
    const { lang, setLang, t } = useLang();
    const [open, setOpen] = useState(false);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header className="sticky top-0 z-40">
            <div className="hidden md:flex items-center justify-between bg-[#2A0505] text-[#E9DAD2] text-xs px-6 lg:px-8 py-2">
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5 text-[#C9A24B]" />{t.topbar.service}</span>
                    <span className="flex items-center gap-1.5"><Languages className="w-3.5 h-3.5 text-[#C9A24B]" />{t.topbar.languages}</span>
                </div>
                <div className="flex items-center gap-4">
                    <a href={`tel:${CONTACT.phone1}`} data-testid="topbar-phone-primary" className="hover:text-white transition-colors">{CONTACT.phone1}</a>
                    <a href={`tel:${CONTACT.phone2}`} data-testid="topbar-phone-alternate" className="hover:text-white transition-colors">{CONTACT.phone2}</a>
                    <a href={waLink()} target="_blank" rel="noreferrer" data-testid="topbar-whatsapp-link" className="flex items-center gap-1.5 text-[#7BE39A] hover:text-white transition-colors">
                        <MessageCircle className="w-3.5 h-3.5" />WhatsApp
                    </a>
                </div>
            </div>

            <div className="backdrop-blur-xl bg-[#FBF7F3]/85 border-b border-[#EADFD8]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
                    <button onClick={() => go("home")} data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
                        <img src="/logo-mark.png" alt="Brighten Services crest logo" className="h-9 w-auto" />
                        <span className="font-display font-black tracking-tight text-lg leading-none text-[#2A0505]">
                            BRIGHTEN
                            <span className="block text-[10px] font-semibold tracking-[0.35em] text-[#8D1717]">SERVICES</span>
                        </span>
                    </button>

                    <nav data-testid="header-navigation-bar" className="hidden lg:flex items-center gap-7">
                        {NAV.map((n) => (
                            <button key={n.id} data-testid={`nav-link-${n.key}`} onClick={() => go(n.id)} className="text-sm font-medium text-[#5C4A45] hover:text-[#8D1717] transition-colors">
                                {t.nav[n.key]}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center rounded-full border border-[#EADFD8] bg-white p-0.5" role="group" aria-label="Language">
                            {LANGS.map((l) => (
                                <button
                                    key={l.code}
                                    data-testid={`lang-toggle-${l.code}`}
                                    onClick={() => setLang(l.code)}
                                    aria-pressed={lang === l.code}
                                    className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${lang === l.code ? "bg-[#8D1717] text-white" : "text-[#8a756f] hover:text-[#8D1717]"}`}
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>
                        <a
                            href={`tel:${CONTACT.phone1}`}
                            data-testid="header-call-button"
                            className="hidden lg:inline-flex items-center gap-2 bg-[#8D1717] hover:bg-[#6D1010] text-white text-sm font-semibold rounded-full px-5 py-2.5 transition-colors"
                        >
                            <Phone className="w-4 h-4" />{t.cta.call}
                        </a>
                        <button data-testid="mobile-menu-button" onClick={() => setOpen(!open)} aria-label="Menu" className="lg:hidden p-2 text-[#2A0505]">
                            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div data-testid="mobile-menu" className="lg:hidden border-t border-[#EADFD8] bg-[#FBF7F3] px-4 py-4 space-y-1">
                        {NAV.map((n) => (
                            <button key={n.id} data-testid={`mobile-nav-link-${n.key}`} onClick={() => go(n.id)} className="block w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-[#2A0505] hover:bg-[#F3E9E2] transition-colors">
                                {t.nav[n.key]}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </header>
    );
};
